import { SendOutlined } from '@mui/icons-material';
import AddIcon from '@mui/icons-material/Add';
import DensityMediumIcon from '@mui/icons-material/DensityMedium';
import FilterListIcon from '@mui/icons-material/FilterList';
import SaveAltIcon from '@mui/icons-material/SaveAlt';
import SearchIcon from '@mui/icons-material/Search';
import FileDownloadSharpIcon from '@mui/icons-material/FileDownloadSharp';
import { Box, Button } from '@mui/material';
import React from 'react';

import FuelSearchInput from '../fuel-search-input';
import VNDropdownButton from '../VNDropdownButton/VNDropdownButton';

export interface TableToolBarAction {
  key?: string;
  label: string;
  icon?: string;
  variant?: 'text' | 'outlined' | 'contained';
  alignRight?: boolean; // New flag to align buttons on the right
  type?: 'button' | 'search' | 'refresh' | 'export';
  value?: string;
}

interface TableToolbarProps {
  actions?: TableToolBarAction[];
  bulkActions?: TableToolBarAction[];
  showBulkMenu?: boolean;
  bulkMenuLabel?: string;
  onActionClick: (key: string) => void; // Common onClick handler
  onSearchTextChanged?: (value: string) => void; // Common onClick handler
}

// Define available icons for buttons
const ICONS: Record<string, JSX.Element> = {
  filter: <FilterListIcon />,
  search: <SearchIcon />,
  export: <FileDownloadSharpIcon />,
  density: <DensityMediumIcon />,
  add: <AddIcon />,
  send: <SendOutlined />,
};

const TableToolbar: React.FC<TableToolbarProps> = ({
  actions = [],
  bulkActions = [],
  showBulkMenu = true,
  bulkMenuLabel = 'BULK OPTIONS',
  onActionClick,
  onSearchTextChanged,
}) => {
  const [hasSearch, setHasSearch] = React.useState(false);
  const handleSearchTextChanged = (value: string) => {
    setHasSearch(value.length > 0);
  };

  return (
    <Box
      className="vn-table-toolbar"
      display="flex"
      alignItems="center"
      justifyContent="space-between"
      padding="10px 0"
      marginBottom="10px"
    >
      {/* Left Section: Bulk Options & Actions */}
      <Box display="flex" alignItems="center" gap={3} width="60%">
        {showBulkMenu && bulkActions.length > 0 && (
          <Box display="flex" alignItems="center" gap={2}>
            <VNDropdownButton items={bulkActions} color="success" onClick={(key) => onActionClick(key)} label={bulkMenuLabel} />
          </Box>
        )}
        <Box display="flex" gap={3} width="70%">
          {actions
            .filter((action) => !action.alignRight)
            .map((action) => {
              switch (action.type) {
                case 'search':
                  return (
                    <FuelSearchInput
                      key={action.key}
                      onChange={onSearchTextChanged}
                      updateValue={handleSearchTextChanged}
                      onSearch={(value) => onActionClick(`${action.key}:${value}`)}
                      handleOnClear={() => onActionClick(`${action.key}:`)} // Clear action
                      showClearIcon={hasSearch}
                    />
                  );
                case 'refresh':
                  return (
                    <div key={action.key}>
                      <i className="icon material-icons refresh-icon" onClick={() => onActionClick(action.key)}>
                        refresh
                      </i>
                    </div>
                  );
                default:
                  return (
                    <Button
                      key={action.key}
                      variant={action.variant || 'text'}
                      startIcon={ICONS[action.icon]}
                      onClick={() => onActionClick(action.key)}
                    >
                      {action.label}
                    </Button>
                  );
              }
            })}
        </Box>
      </Box>

      {/* Right Section: alignRight Actions */}
      <Box display="flex" justifyContent="flex-end" width="40%" gap={1}>
        {actions
          .filter((action) => action.alignRight)
          .map((action) => {
            switch (action.type) {
              case 'search':
                return (
                  <FuelSearchInput
                    key={action.key}
                    onChange={onSearchTextChanged}
                    updateValue={handleSearchTextChanged}
                    onSearch={(value) => onActionClick(`${action.key}:${value}`)}
                    handleOnClear={() => onActionClick(`${action.key}:`)} // Clear action
                    showClearIcon={hasSearch}
                  />
                );
              case 'refresh':
                return (
                  <div key={action.key}>
                    <i className="icon material-icons refresh-icon" onClick={() => onActionClick(action.key)}>
                      refresh
                    </i>
                  </div>
                );
              case 'export':
                return (
                  <div key={action.key}>
                    <i className="icon material-icons refresh-icon" onClick={() => onActionClick(action.key)}>
                      file_download
                    </i>
                  </div>
                );
              default:
                return (
                  <Button
                    key={action.key}
                    variant={action.variant || 'text'}
                    startIcon={ICONS[action.icon]}
                    onClick={() => onActionClick(action.key)}
                    color="success"
                  >
                    {action.label}
                  </Button>
                );
            }
          })}
      </Box>
    </Box>
  );
};

export default TableToolbar;
